import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { SkeletonModule } from 'primeng/skeleton';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, of, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { NotificationsService } from 'app/features/user/services/notifications.service';
import { AuthService } from 'app/features/auth/services/auth.service';
import { Notification } from 'app/features/user/models/notifications.model';
import { NotificationNavigationService } from 'app/features/user/notifications/notification-navigation.service';

@Component({
  selector: 'app-navbar-notifications',
  standalone: true,
  imports: [DatePipe, SkeletonModule],
  templateUrl: './navbar-notifications.component.html',
  styleUrl: './navbar-notifications.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class NavbarNotificationsComponent {
  private notificationsService = inject(NotificationsService);
  private notificationNavigationService = inject(NotificationNavigationService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private toaster = inject(ToastrService);
  private destroyRef = inject(DestroyRef);
  private el = inject(ElementRef);

  isOpen = signal(false);
  loading = signal(false);
  notifications = signal<Notification[]>([]);

  private viewed = signal(false);
  user = computed(() => this.auth.currentUserSignal()?.user);
  hasUnviewed = computed(() => {
    if (this.viewed()) return false;
    const u = this.user() as Record<string, unknown> | undefined;
    return !!(u?.['has_unviewed_notifications'] as boolean);
  });

  onDocumentClick(event: Event): void {
    if (!this.el.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }

  onBellClick(event: Event): void {
    if (this.isOpen()) {
      this.isOpen.set(false);
      return;
    }

    this.isOpen.set(true);

    if (this.hasUnviewed()) {
      this.viewed.set(true);
      this.notificationsService
        .markAsViewed()
        .pipe(
          tap(() => this.auth.patchStoredUser({ has_unviewed_notifications: false })),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }

    this.loadNotifications();
  }

  onPanelClick(event: Event): void {
    event.stopPropagation();
  }

  onItemClick(notification: Notification): void {
    if (!notification.is_read) {
      this.notificationsService
        .markAsRead(notification.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.notifications.update((list) =>
              list.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
            );
            // this.toaster.success(res?.message);
          },
          error: (err) => {
            this.toaster.error(err?.error?.message);
          },
        });
    }

    // if (notification?.type?.includes('service_')) {
    //   this.router.navigate(['/service', notification?.data?.id]);
    // }
    // if (notification?.type?.includes('portfolio_')) {
    //   this.router.navigate(['/profile', this.user()?.username]);
    // }
    this.notificationNavigationService.navigate(
      notification.type,
      notification.data,
      this.user()?.username
    );
    this.isOpen.set(false);
  }

  onNavigateToNotificationsPage(): void {
    this.router.navigate(['/notifications']);
    this.isOpen.set(false);
  }

  hasData(notification: Notification): boolean {
    return !!notification?.type && !!notification.data?.id;
  }

  loadNotifications(): void {
    this.loading.set(true);
    this.notificationsService
      .getNotifications({ page: 1, per_page: 5 })
      .pipe(
        tap(() => this.loading.set(true)),
        catchError(() => of(null)),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        if (res) {
          this.notifications.set(res.data);
          console.log(res.data);
        }
      });
  }
}
